import {announce} from '@github-ui/aria-live'
import {testIdProps} from '@github-ui/test-id-props'
import {CheckIcon, PlusIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useCallback, useEffect, useMemo, useState} from 'react'

import type {IssueField} from '../api/common-contracts'
import {checkFieldCollision} from '../helpers/field-rename-helper'
import {useControlledRef} from '../hooks/common/use-controlled-ref'
import {useViews} from '../hooks/use-views'
import {useAllColumns} from '../state-providers/columns/use-all-columns'
import {useIssueFieldsQuery} from '../state-providers/issue-fields/use-issue-fields-query'
import {useAddFieldModal} from '../state-providers/modals/use-add-field-modal'
import {IssueFieldResources} from '../strings'
import {getColumnIcon} from './column-detail-helpers'
import styles from './issue-fields-quick-add.module.css'
import {useAddField} from './table/hooks/use-add-field'

const ISSUE_FIELD_QUICK_ADD_MAX_FIELDS = 3

const ImportState = {
  Loading: 'loading',
  Success: 'success',
  Error: 'error',
  Idle: 'idle',
}
type ImportState = ObjectValues<typeof ImportState>
const EMPTY_ISSUE_FIELDS: Array<IssueField> = []
export function IssueFieldsQuickAdd({onSeeMore, onClose}: {onSeeMore: () => void; onClose: () => void}) {
  const {data: suggestedIssueFields = EMPTY_ISSUE_FIELDS} = useIssueFieldsQuery()
  const {allColumns} = useAllColumns()

  const issueFieldIds = useMemo(() => {
    return allColumns
      .map(col => col.issueFieldId)
      .filter((id): id is number => id != null)
      .join(',')
  }, [allColumns])

  const addedIssueFieldIds = useMemo(() => {
    return new Set(issueFieldIds.split(',').map(id => Number(id)))
  }, [issueFieldIds])

  const [quickAddFields, setQuickAddFields] = useState<Array<IssueField>>(() =>
    suggestedIssueFields.slice(0, ISSUE_FIELD_QUICK_ADD_MAX_FIELDS),
  )

  useEffect(() => {
    // Only update quick add fields if they haven't been set yet to avoid auto-closing the quick-add UI after fields are added
    if (quickAddFields.length === 0 && suggestedIssueFields.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQuickAddFields(suggestedIssueFields.slice(0, ISSUE_FIELD_QUICK_ADD_MAX_FIELDS))
    }
  }, [suggestedIssueFields, quickAddFields.length])

  if (quickAddFields.length === 0) return null

  return (
    <IssueFieldsQuickAddInternal
      issueFields={quickAddFields}
      hasMoreFields={suggestedIssueFields.length > 0 && quickAddFields.length === ISSUE_FIELD_QUICK_ADD_MAX_FIELDS}
      onSeeMore={onSeeMore}
      onClose={onClose}
      addedIssueFieldIds={addedIssueFieldIds}
    />
  )
}

export function IssueFieldsQuickAddInternal({
  issueFields,
  hasMoreFields,
  onSeeMore,
  onClose,
  addedIssueFieldIds,
}: {
  issueFields: Array<IssueField>
  hasMoreFields: boolean
  onSeeMore: () => void
  onClose: () => void
  addedIssueFieldIds: Set<number>
}) {
  const {currentView} = useViews()

  return (
    <div {...testIdProps('issue-fields-quick-add')} className={styles.Container}>
      <h3 className={styles.Heading}>{IssueFieldResources.QuickAdd.heading}</h3>
      <p className={styles.Description}>{IssueFieldResources.QuickAdd.description}</p>
      <div className={styles.FieldContainer}>
        {issueFields.map(field => (
          <IssueFieldsQuickAddRow
            issueFieldAdded={addedIssueFieldIds.has(field.id)}
            key={field.id}
            field={field}
            viewNumber={currentView?.number}
          />
        ))}
      </div>
      <div className={styles.Footer}>
        {hasMoreFields && (
          <Button size="small" onClick={onSeeMore}>
            {IssueFieldResources.QuickAdd.seeMoreButtonLabel}
          </Button>
        )}
        <Button
          aria-label={IssueFieldResources.QuickAdd.closeButtonAriaLabel}
          variant={hasMoreFields ? 'invisible' : 'default'}
          size="small"
          onClick={onClose}
        >
          {IssueFieldResources.QuickAdd.closeButtonLabel}
        </Button>
      </div>
    </div>
  )
}

function IssueFieldsQuickAddRow({
  issueFieldAdded,
  field,
  viewNumber,
}: {
  issueFieldAdded: boolean
  field: IssueField
  viewNumber?: number
}) {
  const {importIssueFields} = useAddField()
  const Icon = getColumnIcon(field.dataType)
  const [importStateRef, setImportStateRef] = useControlledRef<ImportState>()
  const {allColumnsRef} = useAllColumns()
  const {openRenameDialog} = useAddFieldModal()

  const performFieldImport = useCallback(async () => {
    setImportStateRef(ImportState.Loading)

    await importIssueFields.perform({
      issueFieldIds: [field.id],
      viewNumber,
    })

    if (importIssueFields.status.current.status === 'failed') {
      setImportStateRef(ImportState.Error)
      announce(IssueFieldResources.QuickAdd.addFieldError(field.name))
      return
    }

    setImportStateRef(ImportState.Success)
    announce(IssueFieldResources.QuickAdd.addFieldSuccess(field.name))
  }, [viewNumber, importIssueFields, setImportStateRef, field])

  const addIssueField = useCallback(async () => {
    if (importStateRef.current === ImportState.Success || importStateRef.current === ImportState.Loading) return

    const collision = checkFieldCollision(field, allColumnsRef.current)
    if (collision) {
      openRenameDialog({
        column: collision,
        issueFieldId: field.id,
        source: 'quick-add',
        onRenameConfirm: async () => {
          performFieldImport()
        },
        onRenameCancel: () => {
          setImportStateRef(ImportState.Idle)
        },
      })
      return
    }

    await performFieldImport()
  }, [importStateRef, field, allColumnsRef, performFieldImport, openRenameDialog, setImportStateRef])

  // If the field was added externally (e.g. through the dialog), update the state to success
  useEffect(() => {
    if (issueFieldAdded && importStateRef.current !== ImportState.Success) {
      setImportStateRef(ImportState.Success)
      announce(IssueFieldResources.QuickAdd.addFieldSuccess(field.name))
    }
  }, [issueFieldAdded, importStateRef, setImportStateRef, field.name])

  return (
    <div className={styles.FieldRow} key={field.id}>
      <div className={styles.FieldRowContent}>
        <Icon className={styles.FieldIcon} />
        <span className={styles.FieldName}>{field.name}</span>
        {
          // eslint-disable-next-line react-hooks/refs
          importStateRef.current !== ImportState.Success && (
            <Button
              aria-label={IssueFieldResources.QuickAdd.addFieldButtonAriaLabel(field.name)}
              leadingVisual={PlusIcon}
              size="small"
              onClick={() => addIssueField()}
            >
              {IssueFieldResources.QuickAdd.addFieldButtonLabel}
            </Button>
          )
        }
        {
          // eslint-disable-next-line react-hooks/refs
          importStateRef.current === ImportState.Success && (
            <div {...testIdProps(`issue-fields-quick-add-${field.name}-added`)} className={styles.FieldRowSuccess}>
              <CheckIcon size="small" />
              <span>{IssueFieldResources.QuickAdd.addedFieldInlineMessage}</span>
            </div>
          )
        }
      </div>
      {
        // eslint-disable-next-line react-hooks/refs
        importStateRef.current === ImportState.Error && (
          <div className={styles.FieldRowError}>
            <InlineMessage size="small" variant="critical">
              {IssueFieldResources.QuickAdd.addFieldError(field.name)}
            </InlineMessage>
          </div>
        )
      }
    </div>
  )
}

try{ IssueFieldsQuickAdd.displayName ||= 'IssueFieldsQuickAdd' } catch {}
try{ IssueFieldsQuickAddInternal.displayName ||= 'IssueFieldsQuickAddInternal' } catch {}
try{ IssueFieldsQuickAddRow.displayName ||= 'IssueFieldsQuickAddRow' } catch {}
try{ Icon.displayName ||= 'Icon' } catch {}